import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router';

import { APP_ROUTE } from '@/app/app.route.constant';
import {
  GET_ME_QUERY_KEY,
  getMeQueryOptions,
} from '@/features/auths/api/get-me.api';
import { signInMutationOptions } from '@/features/auths/api/sign-in.api';
import { signOutMutationOptions } from '@/features/auths/api/sign-out.api';
import type { SigninPayload } from '@/features/auths/schemas/sign-in.schema';
import { authLocalStorage } from '@/features/auths/utils/auth-local-storage';

import { type AuthContext, authContext } from './auth-context';

/**
 * Provides authentication state + actions to the entire dashboard via React context.
 *
 * - Tracks session presence via the access-token key in localStorage.
 * - Fetches /auth/me when a token exists.
 * - Exposes signIn / signOut mutations that handle token persistence + cache invalidation.
 *
 * Must be rendered inside a QueryClientProvider.
 */
export default function AuthProvider({ children }: React.PropsWithChildren) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [hasSession, setHasSession] = useState(() =>
    Boolean(authLocalStorage.getAccessToken())
  );

  const getMeQuery = useQuery({
    ...getMeQueryOptions(),
    enabled: hasSession,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const user = getMeQuery.data ?? null;
  const isLoadingAuth = hasSession && getMeQuery.isPending;
  const refetchGetMe = getMeQuery.refetch;

  const isSuperadminRole = user?.role === 'superadmin';
  const isAdminRole = user?.role === 'admin';
  const isEditorRole = user?.role === 'editor';

  const handleSignOutSettled = useCallback(() => {
    authLocalStorage.clearAuthTokens();
    setHasSession(false);
    queryClient.removeQueries({ queryKey: [GET_ME_QUERY_KEY] });
  }, [queryClient]);

  const signOutMutation = useMutation({
    ...signOutMutationOptions(),
    onSettled: handleSignOutSettled,
  });

  const signInMutation = useMutation({
    ...signInMutationOptions(),
    onError: () => {
      toast.error('Email atau password salah.');
    },
    onSuccess: data => {
      authLocalStorage.setAccessToken(data.accessToken);
      authLocalStorage.setRefreshToken(data.refreshToken);
      authLocalStorage.setUser(data.user);
      setHasSession(true);
      queryClient.invalidateQueries({ queryKey: [GET_ME_QUERY_KEY] });

      const role = data.user?.role;
      const capitalized = role
        ? role.charAt(0).toUpperCase() + role.slice(1)
        : '';
      toast.success(
        capitalized ? `Welcome back, ${capitalized}!` : 'Signed in'
      );

      const redirectTo = searchParams.get('redirectTo');
      navigate(redirectTo || APP_ROUTE.ANALYTICS.DASHBOARD.getHref(), {
        replace: true,
      });
    },
  });

  const signIn = useCallback(
    async (params: SigninPayload) => {
      await signInMutation.mutateAsync(params);
    },
    [signInMutation]
  );

  const signOut = useCallback(async () => {
    const refreshToken = authLocalStorage.getRefreshToken();
    if (refreshToken) {
      try {
        await signOutMutation.mutateAsync({ refresh_token: refreshToken });
      } catch {
        // Ignore — onSettled still clears local state.
      }
    } else {
      handleSignOutSettled();
    }
    navigate(APP_ROUTE.AUTH.SIGN_IN.getHref(), { replace: true });
  }, [signOutMutation, handleSignOutSettled, navigate]);

  const value = useMemo(
    (): AuthContext => ({
      user,
      isLoadingAuth,
      isAuthenticated: user !== null && hasSession && !isLoadingAuth,
      isSigningIn: signInMutation.isPending,
      isSigningOut: signOutMutation.isPending,
      isFetchUser: getMeQuery.isFetching,
      isSuperadminRole,
      isAdminRole,
      isEditorRole,
      signIn,
      signOut,
      refetchUser: refetchGetMe,
    }),
    [
      user,
      hasSession,
      isLoadingAuth,
      signInMutation.isPending,
      signOutMutation.isPending,
      getMeQuery.isFetching,
      isSuperadminRole,
      isAdminRole,
      isEditorRole,
      signIn,
      signOut,
      refetchGetMe,
    ]
  );

  return <authContext.Provider value={value}>{children}</authContext.Provider>;
}
